import React, { useEffect, useState } from 'react';

import { StripeConnectedAccountInfo } from '@flipdish/api-client-typescript';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import {
  closeNotifySaving,
  notifyError,
  NotifyProps,
  notifySaved,
  notifySaving,
} from '../../../../../layouts/Notify/actions';
import { bankingServices } from '../../../../../services/banking.service';
import { Button } from '../../../../../ui/Button';

export type OuterProps = {
  appId: string;
  stripeAccount: StripeConnectedAccountInfo;
  getBankAccountDetails: () => void;
};

type Props = OuterProps & MappedDispatch;

const payoutIntervals = ['Manual', 'Daily', 'Weekly', 'Monthly'];

const StyledSelect = styled(Select)({
  minWidth: 140,
});

const PayoutInterval = (props: Props) => {
  const {
    appId,
    closeNotifySaving,
    getBankAccountDetails,
    notifyError,
    notifySaved,
    notifySaving,
    stripeAccount,
  } = props;
  const { StripeId, PayoutScheduleInterval } = stripeAccount;
  const [selectedInterval, setSelectedInterval] = useState(PayoutScheduleInterval || 'Manual');

  useEffect(() => {
    setSelectedInterval(PayoutScheduleInterval || 'Manual');
  }, [PayoutScheduleInterval]);

  const { mutateAsync: savePayoutSchedule, isPending } = useMutation({
    mutationFn: async () => {
      notifySaving();
      return await bankingServices.updatePayoutSchedule(
        appId,
        StripeId!,
        selectedInterval as string
      );
    },
    onSuccess: () => {
      getBankAccountDetails();
      closeNotifySaving();
      notifySaved();
    },
    onError: () => {
      closeNotifySaving();
      notifyError({ message: 'Error_please_try_again_later', translate: true });
    },
  });

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <FormControl variant="outlined" size="small">
        <InputLabel id="payout-interval-label">
          <Translate id="Payout_Interval" />
        </InputLabel>
        <StyledSelect
          labelId="payout-interval-label"
          value={selectedInterval}
          onChange={(e) => setSelectedInterval(e.target.value as string)}
          label={<Translate id="Payout_Interval" />}
        >
          {payoutIntervals.map((interval) => (
            <MenuItem key={interval} value={interval}>
              {interval}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      <Button
        disabled={isPending || selectedInterval === PayoutScheduleInterval}
        variant="contained"
        color="primary"
        onClick={() => savePayoutSchedule()}
        fdKey="save-payout-interval"
      >
        <Translate id="Save" />
      </Button>
    </Box>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  closeNotifySaving: () => dispatch(closeNotifySaving()),
  notifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  notifySaving: () => dispatch(notifySaving({ persist: true })),
  notifySaved: () => dispatch(notifySaved()),
});

const EnhancedComponent = connect(null, mapDispatchToProps)(PayoutInterval);
export { EnhancedComponent as PayoutInterval };
